/**
 * Variation Recording Message Constants
 * Message types and configurations specific to the variation recording workflow
 */

// Import base constants - no circular dependency since base doesn't import anything
import {
  MESSAGE_PRIORITY,
  MESSAGE_SECURITY_LEVELS,
  MESSAGE_FLOW,
  MESSAGE_BUSINESS_HOURS
} from './baseMessageConstants';

// =========================================================
// VARIATION MESSAGE TYPE DEFINITIONS
// =========================================================

/**
 * Variation Recording Workflow Message Types
 */
export const VARIATION_MESSAGE_TYPES = {
  VARIATION_RECORDING_POSTED: 'VARIATION_RECORDING_POSTED',
  VARIATION_STATUS_UPDATE: 'VARIATION_STATUS_UPDATE',
  VARIATION_REVIEW_REQUEST: 'VARIATION_REVIEW_REQUEST',
  VARIATION_REVIEW_RESPONSE: 'VARIATION_REVIEW_RESPONSE',
  VARIATION_RETAKE_REQUEST: 'VARIATION_RETAKE_REQUEST',
  VARIATION_PAYMENT_ENABLED: 'VARIATION_PAYMENT_ENABLED'
};

// =========================================================
// VARIATION RECORDING SPECIFIC CONSTANTS
// =========================================================

/**
 * Retake Reason Types
 * Categorized reasons for requesting a retake of a variation recording
 */
export const RETAKE_REASONS = {
  AUDIO_QUALITY: 'AUDIO_QUALITY',
  PERFORMANCE: 'PERFORMANCE',
  PRONUNCIATION: 'PRONUNCIATION',
  PACING: 'PACING',
  EMOTION: 'EMOTION',
  SCRIPT_ERROR: 'SCRIPT_ERROR',
  BACKGROUND_NOISE: 'BACKGROUND_NOISE',
  VOLUME_ISSUES: 'VOLUME_ISSUES',
  ACCENT_ADJUSTMENT: 'ACCENT_ADJUSTMENT',
  OTHER: 'OTHER'
};

/**
 * Retake Reason Definitions
 * Detailed descriptions and guidance for each retake reason
 */
export const RETAKE_REASON_CONFIG = {
  [RETAKE_REASONS.AUDIO_QUALITY]: {
    displayName: 'Audio Quality Issues',
    description: 'Technical problems with the audio recording',
    examples: [
      'Clipping or distortion',
      'Poor microphone quality',
      'Echo or reverb issues',
      'Digital artifacts'
    ],
    icon: 'volume up',
    severity: 'HIGH'
  },
  [RETAKE_REASONS.PERFORMANCE]: {
    displayName: 'Performance Issues',
    description: 'Acting or delivery needs improvement',
    examples: [
      'Lacks emotion or energy',
      'Over-acting or under-acting',
      'Character voice inconsistent',
      'Unnatural delivery'
    ],
    icon: 'theater masks',
    severity: 'MEDIUM'
  },
  [RETAKE_REASONS.PRONUNCIATION]: {
    displayName: 'Pronunciation Errors',
    description: 'Words or names pronounced incorrectly',
    examples: [
      'Character names wrong',
      'Location names incorrect',
      'Technical terms mispronounced',
      'Accent inconsistencies'
    ],
    icon: 'language',
    severity: 'HIGH'
  },
  [RETAKE_REASONS.PACING]: {
    displayName: 'Pacing Issues',
    description: 'Speed or rhythm needs adjustment',
    examples: [
      'Reading too fast',
      'Reading too slow',
      'Unnatural pauses',
      'Rushed dialogue'
    ],
    icon: 'clock',
    severity: 'MEDIUM'
  },
  [RETAKE_REASONS.EMOTION]: {
    displayName: 'Wrong Emotional Tone',
    description: 'Emotional delivery doesn\'t match the scene',
    examples: [
      'Wrong mood for scene',
      'Lacks required intensity',
      'Emotional transitions unclear',
      'Tone doesn\'t match character'
    ],
    icon: 'heart',
    severity: 'MEDIUM'
  },
  [RETAKE_REASONS.SCRIPT_ERROR]: {
    displayName: 'Script Errors',
    description: 'Deviations from the written text',
    examples: [
      'Words omitted',
      'Words added',
      'Lines skipped',
      'Wrong dialogue read'
    ],
    icon: 'file text',
    severity: 'HIGH'
  },
  [RETAKE_REASONS.BACKGROUND_NOISE]: {
    displayName: 'Background Noise',
    description: 'Unwanted sounds in the recording',
    examples: [
      'Traffic noise',
      'Air conditioning hum',
      'Computer fan noise',
      'External voices'
    ],
    icon: 'bullhorn',
    severity: 'HIGH'
  },
  [RETAKE_REASONS.VOLUME_ISSUES]: {
    displayName: 'Volume Problems',
    description: 'Audio levels need adjustment',
    examples: [
      'Too quiet overall',
      'Too loud/peaking',
      'Inconsistent levels',
      'Needs normalization'
    ],
    icon: 'sliders horizontal',
    severity: 'MEDIUM'
  },
  [RETAKE_REASONS.ACCENT_ADJUSTMENT]: {
    displayName: 'Accent Adjustment Needed',
    description: 'Character accent or dialect needs work',
    examples: [
      'Accent too strong',
      'Accent not strong enough',
      'Accent inconsistent',
      'Wrong regional dialect'
    ],
    icon: 'globe',
    severity: 'MEDIUM'
  },
  [RETAKE_REASONS.OTHER]: {
    displayName: 'Other',
    description: 'Other reasons (specify in notes)',
    examples: [
      'See detailed feedback',
      'Multiple minor issues',
      'Creative direction change'
    ],
    icon: 'ellipsis horizontal',
    severity: 'LOW'
  }
};

// =========================================================
// VARIATION MESSAGE TYPE CONFIGURATION
// =========================================================

/**
 * Variation Message Type Configuration
 * Maps each variation message type to its properties
 */
export const VARIATION_MESSAGE_TYPE_CONFIG = {
  [VARIATION_MESSAGE_TYPES.VARIATION_RECORDING_POSTED]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.VOICE_ACTOR_TO_BOOK_CREATOR,
    icon: 'microphone',
    color: 'teal',
    displayName: 'New Variation Recording',
    requiresAuth: true,
    requiresProfile: true,
    payloadSchema: {
      required: [
        'recordedCharacterId',        // RecordedCharacter UUID
        'characterVariationId',       // CharacterVariation UUID (also sampleId, snippetId)
        'version'                     // Version number
      ],
      optional: [
        'voiceActorNotes'            // Any notes from VA
      ]
    },
    messageTemplate: 'New recording posted for variation {characterVariationId} (Version {version})'
  },

  [VARIATION_MESSAGE_TYPES.VARIATION_STATUS_UPDATE]: {
    priority: MESSAGE_PRIORITY.MEDIUM,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.SYSTEM_TO_USER,
    icon: 'info circle',
    color: 'blue',
    displayName: 'Variation Status Update',
    requiresAuth: true,
    requiresAudit: false,
    payloadSchema: {
      required: [
        'recordedCharacterId',
        'characterVariationId',
        'status'                      // 'pending_review', 'in_review', 'accepted', 'rejected'
      ],
      optional: [
        'previousStatus'
      ]
    },
    messageTemplate: 'Variation status changed to {status}'
  },

  [VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_REQUEST]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.SYSTEM_TO_USER,
    icon: 'clipboard check',
    color: 'yellow',
    displayName: 'Variation Review Request',
    requiresAuth: true,
    requiresProfile: true,
    requiresAction: true,
    payloadSchema: {
      required: [
        'recordedCharacterId',
        'characterVariationId',
        'voiceActorId'               // Who submitted it
      ],
      optional: [
        'priority',                  // Review priority
        'deadline',                  // Review deadline
        'batchId'                    // If part of batch submission
      ]
    },
    messageTemplate: 'Please review variation {characterVariationId}',
    actions: ['ACCEPT', 'REQUEST_RETAKE', 'DEFER']
  },

  [VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_RESPONSE]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.CONFIDENTIAL,
    flow: MESSAGE_FLOW.BOOK_CREATOR_TO_VOICE_ACTOR,
    icon: 'check circle',
    color: 'green',
    displayName: 'Variation Review Response',
    requiresAuth: true,
    requiresAudit: true,
    payloadSchema: {
      required: [
        'recordedCharacterId',
        'characterVariationId',
        'decision'                   // 'ACCEPTED' or 'NEEDS_RETAKE'
      ],
      optional: [
        'feedback',                  // Detailed feedback
        'qualityScore',              // 1-5 rating
        'technicalNotes',            // Audio quality notes
        'performanceNotes'           // Performance notes
      ]
    },
    messageTemplate: {
      ACCEPTED: 'Your recording has been accepted!',
      NEEDS_RETAKE: 'Please retake recording - see feedback for details'
    }
  },

  [VARIATION_MESSAGE_TYPES.VARIATION_RETAKE_REQUEST]: {
    priority: MESSAGE_PRIORITY.HIGH,
    security: MESSAGE_SECURITY_LEVELS.PRIVATE,
    flow: MESSAGE_FLOW.BOOK_CREATOR_TO_VOICE_ACTOR,
    icon: 'redo',
    color: 'orange',
    displayName: 'Retake Request',
    requiresAuth: true,
    requiresAction: true,
    payloadSchema: {
      required: [
        'recordedCharacterId',
        'characterVariationId',
        'retakeReason'               // One of RETAKE_REASONS values
      ],
      optional: [
        'specificIssues',            // Array of specific issues
        'referenceTimestamps',       // Specific moments in audio
        'desiredChanges',            // What needs to change
        'retakeDeadline',            // When retake is needed by
        'retakeAttempt'              // Which retake attempt this is (1st, 2nd, etc)
      ]
    },
    validRetakeReasons: Object.values(RETAKE_REASONS),
    messageTemplate: 'Retake requested: {retakeReason}'
  },

  [VARIATION_MESSAGE_TYPES.VARIATION_PAYMENT_ENABLED]: {
    priority: MESSAGE_PRIORITY.CRITICAL,
    security: MESSAGE_SECURITY_LEVELS.CRITICAL,
    flow: MESSAGE_FLOW.SYSTEM_TO_USER,
    icon: 'dollar sign',
    color: 'green',
    displayName: 'Payment Enabled',
    requiresAuth: true,
    requiresAudit: true,
    requiresEncryption: true,
    payloadSchema: {
      required: [
        'recordedCharacterId',
        'characterVariationId',
        'paymentId',                 // Payment transaction ID
        'amount',                    // Payment amount
        'currency',                  // USD, etc.
        'paymentStatus'              // 'enabled', 'pending', 'processing', 'completed'
      ],
      optional: [
        'paymentMethod',
        'estimatedPaymentDate',
        'invoiceNumber'
      ]
    },
    messageTemplate: 'Payment of {amount} {currency} enabled for variation'
  }
};

// =========================================================
// VARIATION MESSAGE RATE LIMITING
// =========================================================

/**
 * Variation Message Rate Limiting Configuration
 * Defines hourly and daily limits for variation message types
 */
export const VARIATION_MESSAGE_RATE_LIMITS = {
  [VARIATION_MESSAGE_TYPES.VARIATION_RECORDING_POSTED]: {
    hourly: 20,
    daily: 100
  },
  [VARIATION_MESSAGE_TYPES.VARIATION_STATUS_UPDATE]: {
    hourly: 50,
    daily: 500
  },
  [VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_REQUEST]: {
    hourly: 30,
    daily: 200
  },
  [VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_RESPONSE]: {
    hourly: 30,
    daily: 200
  },
  [VARIATION_MESSAGE_TYPES.VARIATION_RETAKE_REQUEST]: {
    hourly: 15,
    daily: 75
  },
  [VARIATION_MESSAGE_TYPES.VARIATION_PAYMENT_ENABLED]: {
    hourly: 10,
    daily: 50
  }
};

// =========================================================
// VARIATION MESSAGE BUSINESS HOURS
// =========================================================

/**
 * Maps variation message types to business hour restrictions
 */
export const VARIATION_MESSAGE_TYPE_HOURS = {
  [VARIATION_MESSAGE_TYPES.VARIATION_RECORDING_POSTED]: MESSAGE_BUSINESS_HOURS.ALWAYS_AVAILABLE,
  [VARIATION_MESSAGE_TYPES.VARIATION_STATUS_UPDATE]: MESSAGE_BUSINESS_HOURS.ALWAYS_AVAILABLE,
  [VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_REQUEST]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_RESPONSE]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [VARIATION_MESSAGE_TYPES.VARIATION_RETAKE_REQUEST]: MESSAGE_BUSINESS_HOURS.STANDARD,
  [VARIATION_MESSAGE_TYPES.VARIATION_PAYMENT_ENABLED]: MESSAGE_BUSINESS_HOURS.FINANCIAL
};

// =========================================================
// VARIATION MESSAGE TEMPLATE KEYS
// =========================================================

/**
 * Variation-specific message template keys
 */
export const VARIATION_MESSAGE_TEMPLATE_KEYS = {
  // Variation Recording Templates
  FIRST_RECORDING: 'first-recording',
  RETAKE_RECORDING: 'retake-recording',
  BATCH_RECORDING: 'batch-recording',

  // Review Response Templates
  QUICK_ACCEPT: 'quick-accept',
  ACCEPT_WITH_PRAISE: 'accept-with-praise',
  MINOR_RETAKE: 'minor-retake',
  MAJOR_RETAKE: 'major-retake'
};

// =========================================================
// HELPER FUNCTIONS
// =========================================================

/**
 * Helper function to get retake reason display information
 * @param {string} reason - The retake reason constant
 * @returns {object} The reason configuration
 */
export const getRetakeReasonConfig = (reason) => {
  return RETAKE_REASON_CONFIG[reason] || RETAKE_REASON_CONFIG[RETAKE_REASONS.OTHER];
};

/**
 * Get all retake reasons as an array for dropdowns
 * @returns {Array} Array of retake reason objects
 */
export const getRetakeReasonOptions = () => {
  return Object.entries(RETAKE_REASON_CONFIG).map(([value, config]) => ({
    value,
    text: config.displayName,
    description: config.description,
    icon: config.icon
  }));
};

// =========================================================
// EXPORT ALL VARIATION CONSTANTS
// =========================================================

const variationMessages = {
  // Types
  VARIATION_MESSAGE_TYPES,
  RETAKE_REASONS,
  RETAKE_REASON_CONFIG,

  // Configurations
  VARIATION_MESSAGE_TYPE_CONFIG,
  VARIATION_MESSAGE_RATE_LIMITS,
  VARIATION_MESSAGE_TYPE_HOURS,
  VARIATION_MESSAGE_TEMPLATE_KEYS,

  // Helper functions
  getRetakeReasonConfig,
  getRetakeReasonOptions
};

export default variationMessages;
